<script lang="ts">
/** The shape the HUD passes around for a hovered or armed action. */
export interface ActionSummary {
  icon: string;
  name: string;
  skillType?: string;
  cost?: {kind: string; value: number}[];
}

// Pure and framework-free on purpose: specs exercise it directly, without
// mounting the component or reaching into <script setup>'s closed scope.
export function resolveActionTriggerMode(props: {
  selectedAction?: unknown;
  previewAction?: unknown;
  tacticalMovementPhase?: boolean;
  inTacticalPreparation?: boolean;
  waiting?: boolean;
  inFight?: boolean;
}): 'action' | 'preview' | 'tactical' | 'empty' | 'waiting' | 'default' {
  if (props.selectedAction) return 'action';
  if (props.previewAction) return 'preview';
  if (props.tacticalMovementPhase) return 'tactical';
  // Out in the world, a spent movement phase has nothing left to offer and the
  // trigger gets out of the way. Inside a fight it is the only control that
  // ends the turn, so hiding it strands the player until the phase deadline
  // runs out — every single movement phase.
  if (props.inTacticalPreparation && !props.inFight) return 'empty';
  if (props.waiting) return 'waiting';
  return 'default';
}
</script>

<script setup lang="ts">

import {computed, ref} from "vue";
import {useI18n} from "vue-i18n";
import {useTypewriter} from "@/composables/useTypewriter";
import {accentForSkillType, DEFAULT_ACCENT} from "@/constants/skillTypeAccent";
import TerminalCaption from "@/components/shared/TerminalCaption.vue";
import {resolveActionCosts} from "@/utils/actionCost";
import TriggerEndTurn from '@/assets/images/action-area/TriggerEndTurn.png';
import TriggerRollDice from '@/assets/images/action-area/TriggerRollDice.png';
import TriggerToSafe from '@/assets/images/action-area/TriggerToSafe.png';
import TriggerBackpack from '@/assets/images/action-area/TriggerBackpack.png';
import TacticalMovementIcon from '@/assets/icons/tactical-movement.png';

const Images = {
  EndTurn: TriggerEndTurn,
  RollDice: TriggerRollDice,
  ToSafe: TriggerToSafe,
  Backpack: TriggerBackpack,
  TacticalMovement: TacticalMovementIcon,
};

// Image Enum
const ImagesEnum = {
  EndTurn: 'EndTurn',
  RollDice: 'RollDice',
  ToSafe: 'ToSafe',
  Backpack: 'Backpack',
  TacticalMovement: 'TacticalMovement'
} as const;

// More explicit type extraction
type TypeOfImageEnum = (typeof ImagesEnum)[keyof typeof ImagesEnum];

interface Props {
  id?: any;
  image?: TypeOfImageEnum | null;
  disabled?: boolean;
  title?: string;
  selectedAction?: ActionSummary | null;
  previewAction?: ActionSummary | null;
  tacticalMovementPhase?: boolean;
  inTacticalPreparation?: boolean;
  chargesPlaced?: number;
  chargeCount?: number;
  waiting?: boolean;
  inFight?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  id: '',
  disabled: false,
  title: '',
  selectedAction: null,
  previewAction: null,
  tacticalMovementPhase: false,
  inTacticalPreparation: false,
  chargesPlaced: 0,
  chargeCount: 1,
  waiting: false,
  inFight: false
});

const emit = defineEmits<{
  select: [id: any];
  endTacticalMove: [];
}>();

const handleClick = () => {
  if (displayMode.value === 'preview' || displayMode.value === 'waiting') return;
  if (displayMode.value === 'tactical') {
    emit('endTacticalMove');
  } else {
    emit('select', props.id);
  }
};

const displayMode = computed(() => resolveActionTriggerMode(props));

const imageSrc = computed(() => {
  if (displayMode.value === 'action') {
    return props.selectedAction.icon;
  }
  if (displayMode.value === 'preview') {
    return props.previewAction.icon;
  }
  if (displayMode.value === 'tactical') {
    return Images.TacticalMovement;
  }
  if (displayMode.value === 'empty') {
    return '';
  }
  return props.image ? Images[props.image] : '';
});

const {t} = useI18n();

// Dwell before a caption commits to screen. Hovering is exploratory, so it waits;
// an armed skill is a decision the player already made, so it barely does.
const HOVER_DWELL_MS = 420;
const PREVIEW_DWELL_MS = 380;
const COMMITTED_DWELL_MS = 90;
const RETARGET_DWELL_MS = 150;

const hovering = ref(false);

const displayTitle = computed(() => {
  if (displayMode.value === 'action') {
    return t('actionTriggers.armedSkill', {skill: props.selectedAction.name});
  }
  if (displayMode.value === 'preview') {
    return props.previewAction.name;
  }
  if (displayMode.value === 'tactical') {
    return t('actionTriggers.tacticalMovement');
  }
  if (displayMode.value === 'waiting') {
    return t('actionTriggers.waitingForOpponent');
  }
  if (displayMode.value === 'default' && props.inFight) {
    return t('actionTriggers.acknowledge');
  }
  return props.title;
});

// Every caption state reduces to "what should be showing right now", which is
// what the typewriter watches. An idle trigger only names itself once the player
// dwells on it; the other states name themselves as soon as they begin.
const captionText = computed(() => {
  switch (displayMode.value) {
    case 'action':
      return props.selectedAction?.name ?? '';
    case 'preview':
      return props.previewAction?.name ?? '';
    case 'tactical':
      return t('actionTriggers.tacticalMovement');
    case 'waiting':
      return t('actionTriggers.waitingForOpponent');
    case 'default':
      if (props.inFight) return t('actionTriggers.acknowledge');
      return hovering.value ? props.title : '';
    default:
      return '';
  }
});

const captionDwell = (retarget: boolean) => {
  if (retarget) return RETARGET_DWELL_MS;
  if (displayMode.value === 'preview') return PREVIEW_DWELL_MS;
  if (displayMode.value === 'default') return HOVER_DWELL_MS;
  return COMMITTED_DWELL_MS;
};

const {typed, open: captionOpen} = useTypewriter(captionText, {dwellMs: captionDwell});

// The caption frames itself in the hovered skill's type colour so it reads as
// belonging to the cell under the cursor. The HUD's own triggers carry no skill
// type, so they fall back to Flow cyan.
const captionAccent = computed(() => {
  if (displayMode.value === 'action') return accentForSkillType(props.selectedAction?.skillType);
  if (displayMode.value === 'preview') return accentForSkillType(props.previewAction?.skillType);
  return DEFAULT_ACCENT;
});

const captionHint = computed(() =>
    displayMode.value === 'action' ? t('actionTriggers.armedHint') : '');

/**
 * What the previewed or armed ability costs, struck across the foot of its own
 * art. It rides the image rather than the caption because the image IS the
 * preview — a player looking at the thing they are about to spend should not
 * have to read a second panel to learn the price.
 *
 * Shows on hover as much as when armed: knowing before committing is the point.
 */
const costs = computed(() => {
  if (displayMode.value === 'action') return resolveActionCosts(props.selectedAction?.cost);
  if (displayMode.value === 'preview') return resolveActionCosts(props.previewAction?.cost);
  return [];
});

const onHoverStart = () => {
  hovering.value = true;
};

const onHoverEnd = () => {
  hovering.value = false;
};

</script>

<template>
  <div class="action__trigger__holder" :class="{ 'trigger--hinted': captionOpen }">
    <div
        class="action__trigger__wrapper"
        :class="{
          'action-selected': displayMode === 'action' || displayMode === 'preview',
          'tactical-preview': displayMode === 'tactical',
          'default-art': displayMode === 'default' || displayMode === 'waiting'
        }"
    >
      <img v-if="imageSrc"
          :src="imageSrc"
          class="action__trigger__image"
          :alt="displayTitle"
          :aria-label="displayTitle"
          :title="displayMode === 'default' ? null : displayTitle"
          @click="handleClick"
          @mouseenter="onHoverStart"
          @mouseleave="onHoverEnd"
          :class="{
            'action__trigger__image--disabled': props.disabled,
            'action__trigger__image--selected': displayMode === 'action' || displayMode === 'preview',
            'action__trigger__image--preview': displayMode === 'preview',
            'action__trigger__image--tactical': displayMode === 'tactical',
            'action__trigger__image--waiting': displayMode === 'waiting'
          }"
      />
      <!-- The price, on the art it belongs to. -->
      <div
          v-if="costs.length"
          class="action__trigger__cost"
          :class="{ 'action__trigger__cost--clear-of-charges': displayMode === 'action' && props.chargeCount > 1 }"
      >
        <span
            v-for="cost in costs"
            :key="cost.tone"
            class="action__trigger__cost-value"
            :style="{ '--cost-rgb': cost.rgb }"
        >{{ cost.value }}</span>
      </div>
      <!-- Charge Counter Badge (show only when action selected and charge count > 1) -->
      <div
        v-if="displayMode === 'action' && props.chargeCount > 1"
        class="charge-counter-badge"
      >
        {{ props.chargesPlaced }}/{{ props.chargeCount }}
      </div>
      <!-- This control cancels the armed ability; casting happens by clicking the map -->
      <button
        v-if="displayMode === 'action'"
        type="button"
        class="cancel-badge"
        :title="t('actionTriggers.cancel')"
        :aria-label="t('actionTriggers.cancel')"
        @click.stop="handleClick"
      >✕</button>
    </div>
    <TerminalCaption
        v-if="captionOpen"
        class="action__trigger__caption"
        :text="typed"
        :accent="captionAccent"
        :hint="captionHint"
        :strong="displayMode === 'action'"
    />
  </div>
</template>

<style scoped>
/* The holder boxes are taller than they are wide and are stacked on different
   z-indexes, so an upper one would swallow clicks aimed at the trigger beneath
   it even where it renders nothing. Only the image is interactive; the frame
   itself never was, so it has no business intercepting. */
.action__trigger__holder {
  width: 100%;
  height: 100%;
  display: flex;
  background: url("@/assets/images/action-area/Holder.png") no-repeat center center;
  background-size: contain;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.action__trigger__wrapper {
  width: 60%;
  height: 60%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}
.action__trigger__wrapper.action-selected {
  width: 100%;
  height: 100%;
  position: relative;
}
/* Idle triggers carry the same full-bleed cinematic art as the tactical and
   armed states, so they get the same room. The radial fade is baked into the
   PNG alpha, so the wrapper opens up and the image keeps the base 60%. */
.action__trigger__wrapper.default-art {
  width: 100%;
  height: 100%;
}
/* The button box is taller than it is wide, so a percentage height would leave
   the image with transparent padding above and below the art — padding that is
   still clickable and stole clicks from the neighbouring trigger. The art is
   square in every state, so squaring the box keeps the hit area equal to the
   disc you can actually see. Sizing below therefore sets width only. */
.action__trigger__image {
  width: 60%;
  height: auto;
  aspect-ratio: 1;
  object-fit: contain;
  transition: transform 0.2s ease-in-out;
  cursor: pointer;
  pointer-events: auto;
}
.action__trigger__image:hover {
  transform: scale(1.05);
  filter: brightness(1.3);
}

.action__trigger__image:active {
  transform: scale(0.95);
}

.action__trigger__image--selected {
  width: 65%;
  mask-image: radial-gradient(circle, rgba(0,0,0,1) 30%, rgba(0,0,0, 0) 67%, rgba(0,0,0,0) 100%);
  -webkit-mask-image: radial-gradient(circle, rgba(0,0,0,1) 30%, rgba(0,0,0, 0) 67%, rgba(0,0,0,0) 100%);
  filter: brightness(1.3) drop-shadow(0 0 8px rgba(var(--game-accent-rgb, 255, 215, 0), 0.6));
  transition: none;

}

.action__trigger__image--selected:hover {
  transform: none;
  filter: brightness(1.4) drop-shadow(0 0 12px rgba(var(--game-accent-rgb, 255, 215, 0), 0.8));
}

.action__trigger__image--selected:active {
  transform: none;
}
.action__trigger__image--preview {
  cursor: default;
  opacity: 0.75;
  filter: brightness(1.05) saturate(0.85) drop-shadow(0 0 6px rgba(255, 255, 255, 0.35));
}

.action__trigger__image--preview:hover {
  filter: brightness(1.05) saturate(0.85) drop-shadow(0 0 6px rgba(255, 255, 255, 0.35));
}

.action__trigger__image--disabled {
  pointer-events: none;
  opacity: 0.5;
  filter: grayscale(100%);
}

.action__trigger__image--waiting {
  cursor: default;
  opacity: 0.6;
  filter: brightness(0.9) saturate(0.7) drop-shadow(0 0 6px rgba(62, 233, 240, 0.35));
  animation: waiting-pulse 1.6s ease-in-out infinite;
}

@keyframes waiting-pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.85;
  }
}

.action__trigger__image--tactical {
  width: 65%;
  cursor: pointer;
  mask-image: radial-gradient(circle, rgba(0,0,0,1) 30%, rgba(0,0,0, 0) 67%, rgba(0,0,0,0) 100%);
  -webkit-mask-image: radial-gradient(circle, rgba(0,0,0,1) 30%, rgba(0,0,0, 0) 67%, rgba(0,0,0,0) 100%);
  filter: brightness(1.1) drop-shadow(0 0 8px rgba(0, 191, 255, 0.6));
  animation: tactical-pulse 2s ease-in-out infinite;
}

.action__trigger__image--tactical:hover {
  filter: brightness(1.3) drop-shadow(0 0 12px rgba(0, 191, 255, 0.9));
  transform: scale(1.05);
}

.action__trigger__image--tactical:active {
  transform: scale(0.95);
}

@keyframes tactical-pulse {
  0%, 100% {
    filter: brightness(1.1) drop-shadow(0 0 8px rgba(0, 191, 255, 0.6));
  }
  50% {
    filter: brightness(1.3) drop-shadow(0 0 12px rgba(0, 191, 255, 0.9));
  }
}

.tactical-preview {
  width: 100%;
  height: 100%;
  position: relative;
}

/* Caption. Two classes on purpose: the parent positions each trigger with a
   single-class rule of its own, and a one-class selector here would lose the
   tie and leave the caption under a neighbouring trigger. */
.action__trigger__holder.trigger--hinted {
  z-index: 30;
}

.action__trigger__caption {
  position: absolute;
  bottom: calc(100% - 12px);
  left: 50%;
  transform: translateX(-50%);
  /* Absolutely positioned, the caption would otherwise shrink to fit the sliver
     of button box beside it and wrap every name onto several lines. */
  width: max-content;
  max-width: 300px;
}

/* The price, along the foot of the previewed art.

   The offset is measured, not guessed. The image is 65% of this square box and
   carries a radial mask that is solid to 30% and gone by 67%; against a
   farthest-corner radius of 0.707 that puts the last solid pixel about 0.22 box
   heights below centre — roughly 28% up from the bottom. Anything lower prints
   on the fade or on nothing at all, which is where these numbers used to sit.

   No plate behind them: this art is the focal object on the HUD and a panel
   across it would read as a dialog. Legibility comes from a dark halo instead. */
.action__trigger__cost {
  position: absolute;
  bottom: 26%;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 0.34rem;
  pointer-events: none;
  z-index: 11;
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.02em;
}

/* The charge counter owns the bottom-right corner whenever a multi-charge skill
   is armed, so the price steps left rather than printing underneath it. */
.action__trigger__cost--clear-of-charges {
  justify-content: flex-start;
  left: 14%;
  right: auto;
}

.action__trigger__cost-value {
  color: rgb(var(--cost-rgb));
  text-shadow:
      0 0 2px rgba(0, 0, 0, 0.98),
      0 1px 2px rgba(0, 0, 0, 0.95),
      0 0 7px rgba(var(--cost-rgb), 0.55);
}

/* Sits on the armed art's top-right corner rather than floating above it, and is
   a real button: cancelling used to require knowing you could click the icon
   itself, because this read as an affordance while ignoring every click. */
.cancel-badge {
  position: absolute;
  top: 15%;
  right: 10%;
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  background: rgba(14, 5, 7, 0.94);
  border: 1px solid rgba(255, 92, 92, 0.85);
  color: #ff9b9b;
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  pointer-events: auto;
  z-index: 12;
  box-shadow: 0 0 10px rgba(255, 92, 92, 0.35), inset 0 0 8px rgba(255, 92, 92, 0.1);
  text-shadow: 0 0 6px rgba(255, 92, 92, 0.7);
  transition: transform 0.15s ease-out, box-shadow 0.15s ease-out, background 0.15s ease-out;
}

.cancel-badge:hover {
  background: rgba(255, 92, 92, 0.22);
  color: #fff;
  transform: scale(1.14);
  box-shadow: 0 0 16px rgba(255, 92, 92, 0.75), inset 0 0 10px rgba(255, 92, 92, 0.2);
}

.cancel-badge:active {
  transform: scale(0.94);
}

.cancel-badge:focus-visible {
  outline: 2px solid rgba(255, 140, 140, 0.95);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .cancel-badge {
    transition: none;
  }
  .cancel-badge:hover,
  .cancel-badge:active {
    transform: none;
  }
}

/* Charge Counter Badge. Bottom-right: both badges show together whenever a
   multi-charge skill is armed, and they used to be stacked in the same corner. */
.charge-counter-badge {
  position: absolute;
  bottom: 13%;
  right: 8%;
  background: linear-gradient(135deg, rgba(var(--game-accent-rgb, 255, 215, 0), 0.95) 0%, rgba(255, 165, 0, 0.95) 100%);
  color: #000;
  font-size: 16px;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 10px rgba(var(--game-accent-rgb, 255, 215, 0), 0.8), inset 0 0 5px rgba(255, 255, 255, 0.4);
  pointer-events: none;
  z-index: 10;
  min-width: 40px;
  text-align: center;
  letter-spacing: 1px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  animation: charge-pulse 1.5s ease-in-out infinite;
}

@keyframes charge-pulse {
  0%, 100% {
    box-shadow: 0 0 10px rgba(var(--game-accent-rgb, 255, 215, 0), 0.8), inset 0 0 5px rgba(255, 255, 255, 0.4);
  }
  50% {
    box-shadow: 0 0 20px rgba(var(--game-accent-rgb, 255, 215, 0), 1), inset 0 0 8px rgba(255, 255, 255, 0.6);
  }
}

</style>